import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ActivityIndicator, ExpandableTable, Icons } from '@hmsexp/component-library';
import { StyleSheet, View } from 'react-native';
import { renderCell } from './renderCell';

const ExpandableGrid = ({ columns, data, getNestedData, sortConfig, handleOnSortClick, sortingInProgress }) => {
  const [loading, setLoading] = useState(false);
  const [expandedRowToFetch, setExpandedRowToFetch] = useState(null);
  const [tableData, setTableData] = useState(data);
  const fetchingRef = useRef(new Set());
  const expandedRowsRef = useRef(new Set());

  useEffect(() => {
    setTableData(prevData => {
      if (prevData === data) return prevData;

      const mergeNestedData = (newData, oldData) => {
        return newData.map(newRow => {
          const oldRow = oldData?.find(r => r.id === newRow.id);
          return oldRow?.subData ? { ...newRow, subData: oldRow.subData } : newRow;
        });
      };

      return mergeNestedData(data, prevData);
    });
  }, [data]);


  const patchColumns = useCallback(
    function patch(cols) {
      if (!cols || !Array.isArray(cols)) return [];

      return cols.map(col => {
        const patched = {
          ...col,
          ...(col.field && {
            Cell: renderCell(sortConfig, handleOnSortClick)
          })
        };

        if (col.columns) patched.columns = patch(col.columns);

        return patched;
      });
    },
    [sortConfig, handleOnSortClick]
  );

  const patchedColumns = useMemo(() => patchColumns(columns), [columns, sortConfig]);

  const insertSubData = (fullData, newSubData, id) => {
    return fullData.map(row => {
      if (row.id === id) {
        return { ...row, subData: newSubData };
      }
      if (row.subData?.length) {
        return { ...row, subData: insertSubData(row.subData, newSubData, id) };
      }
      return row;
    });
  };

  const handleRowExpand = row => {
    if (expandedRowsRef.current.has(row.id)) {
      expandedRowsRef.current.delete(row.id);
    } else {
      expandedRowsRef.current.add(row.id);
      setExpandedRowToFetch(row);
    }
  };

  useEffect(() => {
    if (!expandedRowToFetch) return;

    if (fetchingRef.current.has(expandedRowToFetch.id)) {
      return;
    }

    fetchingRef.current.add(expandedRowToFetch.id);

    const fetchNested = async () => {
      try {
        setLoading(true);
        const nestedRows = await getNestedData(expandedRowToFetch);
        setTableData(prev => insertSubData(prev, nestedRows, expandedRowToFetch.id));
      } catch (e) {
        console.error('Nested fetch failed:', e);
      } finally {
        setLoading(false);
        setExpandedRowToFetch(null);
        fetchingRef.current.delete(expandedRowToFetch.id);
      }
    };
    fetchNested();
  }, [expandedRowToFetch, getNestedData]);

  const renderCollapsibleIcon = useCallback(({ collapsed }) => {
    return collapsed ? <Icons.ChevronRight size='L' /> : <Icons.ChevronDown size='L' />;
  }, []);

  const getCellProps = useCallback(
    ({ column }) => {
      return {
        containerStyle: {
          backgroundColor: column.field === 'total_count' ? '#E9F2FF' : '#fff'
        }
      };
    },
  );

  return (
    <View style={[styles.container, (loading || sortingInProgress) && styles.dimmed]}>
      {(loading || sortingInProgress) && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator height={40} width={40} />
        </View>
      )}

      <ExpandableTable
        columns={patchedColumns}
        collapsibleIconsDirection='left'
        applyRenderRowToNestedRows={true}
        renderCollapsibleIcon={renderCollapsibleIcon}
        data={tableData}
        style={tableStyles}
        getCellProps={getCellProps}
        hasNestedData
        showTitleBar={false}
        maxNestingLevel={0}
        customOnPressIcon={handleRowExpand}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 3,
    display: 'flex',
    opacity: 1,
    pointerEvents: 'auto'
  },
  containerLoading: {
    opacity: 0.4,
    pointerEvents: 'none'
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 999
  },
  dimmed: {
    opacity: 0.4,
    pointerEvents: 'none'
  }
});

const tableStyles = {
  container: {
    borderWidth: 0
  },
  containerStyle: {
    backgroundColor: 'white'
  },
  headerCellWrapper: {
    backgroundColor: '#fff'
  },
  headerContainer: {
    height: 60,
    borderWidth: 0,
    backgroundColor: '#fff'
  },
  headerTextWrapperStyle: {
    backgroundColor: '#fff',
    fontWeight: '800'
  }
};

export default ExpandableGrid;